import React, { useEffect, useState, useContext, useRef } from "react";
import { useForm, useWatch, FormProvider } from "react-hook-form";
import AppContext from "../../App/AppContext";
import TextInput from "../InputFields/TextInput";
import SearchSelectInput from "../InputFields/SearchSelectInput";
import {
  alphabetizeArrayByKey,
  buildAgeRange,
  getFirstName,
  getAgeFromBirthday,
} from "../../utils/helpers";
import SelectInput from "../InputFields/SelectInput";
import TextArea from "../InputFields/TextArea";
import ZipCodeInput from "../InputFields/ZipCodeInput";
import RadioInput from "../InputFields/RadioInput";
import StepCard from "./StepCard";
import VerificationFormStyles from "./VerificationFormStyles";
import InputStyles from "../InputFields/InputStyles";
import EmailInput from "../InputFields/EmailInput";
import PhoneInput from "../InputFields/PhoneInput";
import CheckboxInput from "../InputFields/CheckboxInput";
import BirthdateInput from "../InputFields/BirthdateInput";
import ResponseScreen from "./ResponseScreen";
import {
  sendToCallrail,
  sendToGravityForms,
  sendToCtm,
  submitWordpressStorage,
  verifyInsurance,
  formTracking,
} from "./utils";
import pVerifyPayers from "./pVerifyPayers.json";

export const VerificationFormContext = React.createContext({});

const VerificationForm = (props) => {
  const {
    options: {
      form_title = "",
      primary_color = "",
      providers = [],
      cvf_use_callrail = false,
      cvf_use_gravity_forms = false,
      cvf_use_ctm = false,
      cvf_gravity_form_id = "",
      privacy_policy_link = "",
      contact_phone_number = "",
      pverify_provider_name = "",
    } = {},
  } = useContext(AppContext);

  // Form State
  const [state, setState] = useState({
    activeStep: 1,
    showManualProviderEntryField: false,
    isSubmitting: false,
    submitted: false,
    coverageVerificationData: {},
  });
  const updateState = (newState) => {
    setState((prevState) => ({
      ...prevState,
      ...newState,
    }));
  };

  // Things to do when step changes
  const ref = useRef(null);
  useEffect(() => {
    if (state?.activeStep > 1) {
      ref.current.scrollIntoView({
        behavior: "smooth",
      });
    }
  }, [state?.activeStep]);

  // FORM
  const form = useForm({
    mode: "onTouched",
    reValidateMode: "onChange",
  });
  const { handleSubmit, watch, trigger, errors } = form;

  // Submit
  const submitForm = async (formData) => {
    updateState({ isSubmitting: true });

    // Verification
    let verificationData = [];
    try {
      if (formData?.payer_name && formData?.payer_id) {
        verificationData = await verifyInsurance(formData);
        updateState({ coverageVerificationData: verificationData?.data });
      }
    } catch (error) {
      console.error("pVerify Verification Failed", error);
    }

    // Gravity forms
    if (Boolean(cvf_use_gravity_forms) && !!cvf_gravity_form_id) {
      try {
        const gravityFormsData = await sendToGravityForms(
          formData,
          verificationData,
          cvf_gravity_form_id,
        );
      } catch (error) {
        console.error("Submission to GravityForms Failed:", error);
      }
    }

    // Callrail
    if (Boolean(cvf_use_callrail)) {
      try {
        const callrailData = await sendToCallrail(formData, verificationData);
      } catch (error) {
        console.error("Submission to Callrail Failed:", error);
      }
    }

    // CTM
    if (Boolean(cvf_use_ctm)) {
      try {
        const callrailData = await sendToCtm(formData, verificationData);
      } catch (error) {
        console.error("Submission to CallTrackingMetrics Failed:", error);
      }
    }

    // Wordpress Storage
    // try {
    //   const wpStorage = await submitWordpressStorage(formData);
    // } catch (error) {
    //   console.error("Save to WP Storage Failed:", error);
    // }

    // TRACKING
    formTracking();

    updateState({
      isSubmitting: false,
      submitted: true,
    });
  };

  // Watching Fields
  const [
    payerName,
    patientDob,
    name,
    relationship,
    phoneNumber,
    patientAge,
    is_subscriber_patient,
  ] = watch([
    "payer_name",
    "patient_dob",
    "name",
    "relationship_to_patient",
    "phone_number",
    "patient_age",
    "is_subscriber_patient",
  ]);
  useEffect(() => {
    const payer_pverify_id =
      pVerifyPayers?.find((p) => p?.name === payerName)?.payer_code || "";
    form?.setValue("payer_id", payer_pverify_id);
  }, [payerName]);
  useEffect(() => {
    const age = getAgeFromBirthday(patientDob);
    form?.setValue("patient_age", String(age));
  }, [patientDob]);

  const hasAnimated = useRef({}); // persists across step changes;

  // remove "dental" payers
  const filteredPayers = pVerifyPayers?.filter(
    (payer) => !payer.name.toLowerCase().includes("dental"),
  );

  return (
    <>
      <VerificationFormContext.Provider value={{ state, updateState }}>
        <VerificationFormStyles $primary_color={primary_color}>
          <InputStyles $primary_color={primary_color}>
            <FormProvider {...form}>
              <form onSubmit={handleSubmit(submitForm)} ref={ref}>
                {!state?.submitted ? (
                  <>
                    {state?.activeStep === 1 && (
                      <StepCard
                        step={1}
                        hasAnimated={hasAnimated}
                        title={form_title}
                        copy="Who’s your insurance provider? Once we know that, we can check your benefits and confirm whether your plan is in-network and what it may cover."
                      >
                        {state?.showManualProviderEntryField ? (
                          <div className="cvf__input-container">
                            <p className="cvf__copy">
                              No worries, just enter your provider name below.
                            </p>
                            <TextInput
                              label="Enter Provider Name"
                              required
                              fieldName="payer_name"
                              errorMessage="Please enter your provider."
                              {...form}
                            />
                          </div>
                        ) : (
                          <>
                            <div className="cvf__input-container">
                              <SearchSelectInput
                                label="Provider Name"
                                placeholder="Search for Provider"
                                fieldName="payer_name"
                                errorMessage="Please select a provider. If you don't see yours listed, please click the link below to enter a custom name."
                                displayOwnError={true}
                                customError={{
                                  errorMessage:
                                    "Please check your spelling or search by another name.",
                                  errorOptionsTitle:
                                    "Here are a few suggestions for you:",
                                  errorOptions: [
                                    {
                                      name: "Aetna",
                                    },
                                    {
                                      name: "Blue Cross Blue Shield",
                                    },
                                    {
                                      name: "United Healthcare",
                                    },
                                  ],
                                }}
                                options={filteredPayers}
                                required={true}
                                {...form}
                              />
                            </div>
                          </>
                        )}
                        <div className="cvf__input-container">
                          <SelectInput
                            label="Is the patient the main person on this insurance plan?"
                            fieldName="is_subscriber_patient"
                            placeholder="Please Select Option"
                            options={[
                              {
                                title:
                                  "Yes — The patient is the primary policyholder",
                                value: "true",
                              },
                              {
                                title:
                                  "No — The patient is covered under someone else’s plan",
                                value: "false",
                              },
                            ]}
                            required={true}
                            errorMessage="Prefered contact method is required."
                            {...form}
                          />
                        </div>
                      </StepCard>
                    )}
                    {state?.activeStep == 2 && (
                      <StepCard
                        step={2}
                        hasAnimated={hasAnimated}
                        response={`Perfect, we’ll look into your ${payerName} benefits to verify what's included.`}
                        title={`To help us support you, we’ll collect a few insurance details.`}
                      >
                        <div className="cvf__copy">
                          Please fill in the subscriber information below. If
                          you need assistance please call us at{" "}
                          <a
                            href={`tel:${contact_phone_number}`}
                            style={{
                              color: primary_color,
                              textDecoration: "underline",
                            }}
                          >
                            {contact_phone_number}
                          </a>
                          .
                        </div>
                        <div className="cvf__input-container">
                          <TextInput
                            label="Member ID"
                            required
                            fieldName="member_id"
                            errorMessage="Please enter your member id found on your insurance card."
                            {...form}
                          />
                        </div>
                        <div className="cvf__input-container">
                          <TextInput
                            label="Subscriber First Name"
                            required
                            fieldName="subscriber_first_name"
                            errorMessage="Please enter the first name of the subscriber of the insurance policy."
                            {...form}
                          />
                        </div>
                        <div className="cvf__input-container">
                          <TextInput
                            label="Subscriber Last Name"
                            required
                            fieldName="subscriber_last_name"
                            errorMessage="Please enter the last name of the subscriber of the insurance policy."
                            {...form}
                          />
                        </div>
                        {is_subscriber_patient === "false" && (
                          <>
                            <div className="cvf__input-container">
                              <TextInput
                                label="Patient First Name"
                                required
                                fieldName="patient_first_name"
                                errorMessage="Please enter the first name of the patient of the insurance policy."
                                {...form}
                              />
                            </div>
                            <div className="cvf__input-container">
                              <TextInput
                                label="Patient Last Name"
                                required
                                fieldName="patient_last_name"
                                errorMessage="Please enter the last name of the patient of the insurance policy."
                                {...form}
                              />
                            </div>
                          </>
                        )}
                      </StepCard>
                    )}
                    {state?.activeStep == 3 && (
                      <StepCard
                        step={3}
                        hasAnimated={hasAnimated}
                        response={`Great. Thank you for sharing those insurance details.`}
                        title={`Tell Us a Bit About Who Needs Care`}
                        copy={`Tell us the age of the person receiving care and what kind of treatment is needed, so we can guide you to the right program.`}
                      >
                        <div className="cvf__input-container">
                          <BirthdateInput
                            {...form}
                            label="Patient Date of Birth"
                            fieldName="patient_dob"
                            errorMessage="Date of Birth is required"
                            required={true}
                          />
                        </div>
                        <div className="cvf__input-container">
                          <TextArea
                            label="How can we help?"
                            fieldName="message"
                            errorMessage="Please enter a message."
                            required={true}
                            {...form}
                          />
                        </div>
                      </StepCard>
                    )}
                    {state?.activeStep == 4 && (
                      <StepCard
                        step={4}
                        hasAnimated={hasAnimated}
                        response={`Got it. We’ll keep that in mind as we check available programs
                  for a ${patientAge}-year-old.`}
                        title={`Tell Us About Yourself`}
                        copy={`Is this for you or someone else?`}
                      >
                        <div className="cvf__input-container">
                          <SelectInput
                            label="What is your relationship to the patient?"
                            fieldName="relationship_to_patient"
                            placeholder="Please Select Option"
                            helperText=""
                            options={[
                              { title: "I am the patient.", value: "Patient" },
                              {
                                title: "I am a parent or guardian.",
                                value: "Parent",
                              },
                              {
                                title: "I am a healthcare professional.",
                                value: "Provider",
                              },
                              { title: "Other", value: "Other" },
                            ]}
                            required={true}
                            errorMessage="Relationship is required."
                            {...form}
                          />
                        </div>
                        {!!relationship && relationship !== "Patient" && (
                          <div className="cvf__input-container">
                            <TextInput
                              label="What is your name?"
                              helperText="Full Name"
                              required
                              fieldName="name"
                              errorMessage="Please enter your full name."
                              {...form}
                            />
                          </div>
                        )}
                      </StepCard>
                    )}
                    {state?.activeStep === 5 && (
                      <StepCard
                        step={5}
                        hasAnimated={hasAnimated}
                        response={`${relationshipResponses[relationship]}`}
                        title="How Can We Reach You?"
                        copy="Add your contact information so our admissions team can
                  contact you with your coverage details and nearby options."
                      >
                        <div className="cvf__input-container">
                          <EmailInput
                            label="Email"
                            fieldName="email"
                            helperText="johnsmith@mail.com"
                            placeholder="Type Email"
                            errorMessage="Email address is not valid."
                            required={true}
                            {...form}
                          />
                        </div>
                        <div className="cvf__input-container">
                          <PhoneInput
                            label="Phone"
                            fieldName="phone_number"
                            helperText="555-555-5555"
                            placeholder="Type Phone"
                            required={true}
                            errorMessage="Phone number is not valid."
                            {...form}
                          />
                        </div>
                        <div className="cvf__input-container">
                          <SelectInput
                            label="Prefered Method of Contact"
                            fieldName="contact_method"
                            placeholder="Please Select Contact Method"
                            options={["Call", "Text"].map((item) => {
                              return { title: item, value: item };
                            })}
                            required={true}
                            errorMessage="Prefered contact method is required."
                            {...form}
                          />
                        </div>
                        <div className="cvf__input-container">
                          <ZipCodeInput
                            label="Zip Code"
                            fieldName="zip_code"
                            placeholder="Type Zip Code"
                            helperText="75001"
                            required={true}
                            errorMessage="Zip code is required."
                            {...form}
                          />
                        </div>
                      </StepCard>
                    )}
                    {state?.activeStep === 6 && (
                      <StepCard
                        step={6}
                        hasAnimated={hasAnimated}
                        response={`Thanks for that information. After you submit this form, our team will contact you at ${phoneNumber}.`}
                        title="One Last Thing"
                        copy="If you have a second, let us know how you heard about us. It
                  helps us understand how people find care and connect with our
                  team."
                      >
                        <div className="cvf__input-container">
                          <SelectInput
                            label="How did you hear about us?"
                            fieldName="referral_source"
                            placeholder=""
                            helperText="(Optional)"
                            options={[
                              "Internet",
                              "Hospital",
                              "Insurance",
                              "School",
                              "Other",
                            ].map((item) => {
                              return { title: item, value: item };
                            })}
                            errorMessage="Prefered contact method is required."
                            {...form}
                          />
                        </div>
                        <div className="cvf__input-container">
                          <CheckboxInput
                            fieldName="sms_opt_in"
                            label={
                              <>
                                By providing a telephone number and submitting
                                this form you are consenting to be contacted by
                                SMS text message. Message & data rates may
                                apply. You can reply STOP to opt-out of further
                                messaging, and get more help by sending HELP.
                                {" | "}
                                <a
                                  href={privacy_policy_link}
                                  style={{ textDecoration: "underline" }}
                                >
                                  Privacy Policy
                                </a>
                                {" | "}
                              </>
                            }
                            required={true}
                            {...form}
                          />
                        </div>
                        <div className="cvf__input-container">
                          <CheckboxInput
                            fieldName="insurance_verification_consent"
                            label={
                              <>
                                By submitting this form, you authorize
                                {pverify_provider_name} to collect and use the
                                information you provide, including insurance
                                information, to verify eligibility and coverage
                                through third-party verification services (such
                                as insurance clearinghouses or benefit
                                verification vendors). Insurance verification is
                                performed for administrative and intake purposes
                                only and does not guarantee coverage or payment.
                                Your insurance and health-related information is
                                handled in accordance with applicable privacy
                                laws. Detailed insurance and eligibility
                                information is shared only with authorized
                                personnel involved in intake, admissions,
                                billing, or care coordination. Contact
                                information you provide (such as your name,
                                phone number, and email address) may be used to
                                contact you regarding your inquiry, verification
                                results, or next steps. Limited, non-medical
                                status information may be used in internal
                                systems to facilitate follow-up communication.
                                We do not sell your health information.
                              </>
                            }
                            required={true}
                            {...form}
                          />
                        </div>
                      </StepCard>
                    )}
                  </>
                ) : (
                  <>
                    <ResponseScreen />
                  </>
                )}
              </form>
            </FormProvider>
          </InputStyles>
        </VerificationFormStyles>
      </VerificationFormContext.Provider>
    </>
  );
};
export default VerificationForm;

const relationshipResponses = {
  Patient:
    "Thanks for letting us know. Taking care of yourself is important, and we’ll guide you through the next steps so you know exactly what to expect.",
  Parent:
    "Thanks for sharing that. Supporting your child through this process matters, and we’re here to help make the next steps clear and manageable for you both.",
  Provider:
    "Thank you. We appreciate partnering with you to support this patient, and we’ll help guide you through the remaining steps so we can move forward together.",
  Other:
    "Thanks for sharing that. No matter your connection, we’re here to help you move through the next steps and get the information you need.",
};
